import {
  $KTM,
  BaseListItem,
  BaseListItemsLoader,
  DefaultObjectLoader,
  KeyMetadatas,
  ListKeysLoader,
  TypedObject,
  UserDisplayName
} from "../shared/types";
import {getMemberAuth} from "../shared/auth";
import {JsonObject} from "../shared/json/json-object";
import {JsonProperty} from "../shared/json/json-property";
import {JSON_OBJECT, Type} from "../shared/json/helpers";
import {Rel, SystemKeys} from "../shared/database";
import {md5_uuid} from "../shared/md5";
import {IAudioMetadata} from "music-metadata";
import {PaletteMode, SvgIcon} from "@mui/material";
import {Member} from "../shared/entities";
import {AudioFileOutlined, ImageOutlined, MovieOutlined, StarOutlineOutlined} from "@mui/icons-material";
import {BLUE, GREEN, PURPLE, RED} from "../peppin-shared/colors";
import {signOut} from "@firebase/auth";

export type UserProfile = {
  member: Member,
  metadata: UserMetadata,
}

export async function deleteAccount(): Promise<void> {
  signOut(getMemberAuth().auth)
    .then(() => window.location.href = "/");
}

export function isMe(userProfile: UserProfile): boolean {
  return getMemberAuth()?.getMemberId() === userProfile.member.memberId;
}

@JsonObject()
export class UserMetadata extends TypedObject {

  @JsonProperty()
  backgroundId?: string;

  @JsonProperty()
  avatarUrl?: string;

  @JsonProperty()
  about?: string;

  @JsonProperty()
  contactEnabled?: boolean;

  private static readonly instances = new Map<string, UserMetadata>();

  constructor(private readonly memberId: string) {
    super(null, null);
  }

  static async load(memberId: string): Promise<UserMetadata> {
    let metadata = this.instances.get(memberId);
    if (!metadata) {
      metadata = new UserMetadata(memberId);
      await metadata.load();
      this.instances.set(memberId, metadata);
    }
    return metadata;
  }

  protected getType(): Type<any> {
    return UserMetadata;
  }

  protected basePath(): string | null {
    return "user_metadata";
  }

  createDefaultLoader(): DefaultObjectLoader<UserMetadata> {
    return new DefaultObjectLoader<UserMetadata>(this.basePath(), this.memberId, this.getType(), {shared: true});
  }


}

@JsonObject()
export class Follow extends BaseListItem {

  static createNew(followId: string) {
    return new Follow(getMemberAuth().getMemberId(), Date.now(), followId);
  }

  constructor(creator: string, created: number, followId: string) {
    super(followId, creator, created);
  }

  static readonly FOLLOWERS_REL_NAME = "followers";

  followersRel() {
    return new Rel(Follow.FOLLOWERS_REL_NAME, this.id, this.creator, true);
  }
}

export class Follows extends BaseListItemsLoader<Follow> {

  private static instance: Follows;
  private static readonly memberInstances = new Map<string, Follows>();

  static getInstance(): Follows {
    if (!this.instance) {
      this.instance = new Follows(getMemberAuth().getMemberId());
    }
    return this.instance;
  }

  static getMemberInstance(memberId: string): Follows {
    let instance = this.memberInstances.get(memberId);
    if (!instance) {
      instance = new Follows(memberId);
      this.memberInstances.set(memberId, instance);
    }
    return instance;
  }

  constructor(private readonly memberId: string) {
    super({shared: Boolean(memberId)});
  }

  protected basePath(): string {
    return "follows" + (this.memberId ? "/" + this.memberId : "");
  }

  protected deserializeItem(value: any): Follow {
    return JSON_OBJECT.deserializeObject(value, Follow);
  }

  protected serializeItem(item: Follow): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Follow, item2: Follow): number {
    return 0;
  }

  async addFollow(follow: Follow) {
    await this.addListItem(follow, follow.followersRel());
  }

  async deleteFollow(follow: Follow) {
    await this.deleteListItem(follow, follow.followersRel());
  }
}

export class Followers extends ListKeysLoader {

  private static instance: Followers;
  private static readonly memberInstances = new Map<string, Followers>();

  static getInstance(): Followers {
    if (!this.instance) {
      this.instance = new Followers();
    }
    return this.instance;
  }

  static getMemberInstance(memberId: string): Followers {
    let instance = this.memberInstances.get(memberId);
    if (!instance) {
      instance = new Followers(memberId);
      this.memberInstances.set(memberId, instance);
    }
    return instance;
  }

  constructor(private readonly memberId?: string) {
    super({shared: Boolean(memberId)});
  }

  protected basePath(): string {
    return "follows/" + SystemKeys.REL + "/followers" + (this.memberId ? "/" + this.memberId : "");
  }
}

@JsonObject()
export class ShareScope {

  @JsonProperty()
  memberIds: string[]

  constructor() {
  }
}

export enum Privacy {
  AUTO = "auto", // Anyone with direct link can access, but not shown on profile page. Similar to "anyone with a link".
  MEMBERS = "members", // Anyone currently logged in.
  SCOPED = "scoped", // Anyone in share scope.
  PRIVATE = "private",
  PUBLIC = "public",
}

@JsonObject()
export abstract class BaseItem extends BaseListItem {

  @JsonProperty()
  title: string;

  @JsonProperty()
  artists: string[];

  @JsonProperty()
  description: string;

  @JsonProperty()
  coverImageUrl?: string;

  @JsonProperty()
  privacy: Privacy = Privacy.AUTO;

  @JsonProperty()
  shareScope?: ShareScope;

  constructor(id: string, creator: string, created: number, title?: string) {
    super(id, creator, created);
    this.title = title || this.defaultTitle();
  }

  checkPrivacy(): boolean {
    if (this.privacy === Privacy.PUBLIC || this.privacy === Privacy.AUTO) {
      return true;
    }
    const memberAuth = getMemberAuth();
    if (!memberAuth) {
      return false;
    }
    if (this.privacy === Privacy.MEMBERS) {
      return true;
    }
    if (this.creator === memberAuth.getMemberId()) {
      return true;
    }
    return this.checkScope();
  }

  private checkScope() {
    return false;
  }

  protected abstract defaultTitle(): string;
}

export enum ImageDataType {
  URL = "url",
}

@JsonObject()
export class ImageData {

  @JsonProperty()
  type: ImageDataType = ImageDataType.URL;

  @JsonProperty()
  url: string;
}

export enum VideoDataType {
  URL = "url",
  EMBED_YOUTUBE = "embed_youtube",
}

@JsonObject()
export class VideoData {

  @JsonProperty()
  type: VideoDataType = VideoDataType.URL;

  @JsonProperty()
  url: string;

  embedHtml: string;
}

export enum AudioDataType {
  URL = "url",
}

@JsonObject()
export class AudioData {

  @JsonProperty()
  type: AudioDataType = AudioDataType.URL;

  @JsonProperty()
  url: string;

  @JsonProperty()
  duration: number = 0;

  metadata: IAudioMetadata;
}

@JsonObject()
export class ScratchProjectData {

  @JsonProperty()
  projectId: string;
}

export enum ShowcaseItemType {
  UPLOAD = "_upload",
  PLACEHOLDER = "_placeholder",
  IMAGE = "image",
  VIDEO = "video",
  AUDIO = "audio",
  SCRATCH_PROJECT = "scratch_project",
}

export type ShowcaseItemTypeMetadata = {
  iconType: typeof SvgIcon,
  name: string,
  uploadTitle?: string,
  uploadText?: string,
  supportsEmbed?: boolean,
  embedTitle?: string,
  embedText?: string,
  color: string,
}

export const SHOWCASE_ITEM_TYPE_METADATAS = new KeyMetadatas<ShowcaseItemTypeMetadata>([
  $KTM(ShowcaseItemType.IMAGE, {
    iconType: ImageOutlined,
    name: "Image",
    uploadTitle: "Upload an image",
    uploadText: "Minimum 800px width recommended. Max 10MB each.",
    supportsEmbed: false,
    embedTitle: "Image URL",
    embedText: "Embed images hosted on other platforms.",
    color: RED,
  }),
  $KTM(ShowcaseItemType.VIDEO, {
    name: "Video",
    iconType: MovieOutlined,
    uploadTitle: "Upload a video",
    uploadText: "Max 20MB each.",
    supportsEmbed: true,
    embedTitle: "Video URL",
    embedText: "Only videos hosted on Youtube.com are currently supported.",
    color: PURPLE,
  }),
  $KTM(ShowcaseItemType.AUDIO, {
    name: "Audio",
    iconType: AudioFileOutlined,
    uploadTitle: "Upload an audio file",
    uploadText: "Max 10MB each.",
    supportsEmbed: false,
    embedTitle: "Audio URL",
    embedText: "Embed audio hosted on other platforms, like Soundcloud, and more.",
    color: GREEN,
  }),
  $KTM(ShowcaseItemType.SCRATCH_PROJECT, {
    name: "Scratch Project",
    iconType: StarOutlineOutlined,
    supportsEmbed: true,
    embedTitle: "Embed a Scratch Project",
    embedText: "Only Scratch projects hosted on mit.edu domains are supported.",
    color: BLUE,
  }),
]);

@JsonObject()
export class ShowcaseItem extends BaseItem {

  static readonly UPLOAD = new ShowcaseItem(null, null, null, ShowcaseItemType.UPLOAD);
  static readonly PLACEHOLDER = new ShowcaseItem(null, null, null, ShowcaseItemType.PLACEHOLDER);

  @JsonProperty()
  readonly type: ShowcaseItemType;
  @JsonProperty()
  readonly imageData?: ImageData;
  @JsonProperty()
  readonly videoData?: VideoData;
  @JsonProperty()
  readonly audioData?: AudioData;
  @JsonProperty()
  readonly scratchProjectData?: ScratchProjectData;

  @JsonProperty()
  albumId?: string;

  static createNew(type: ShowcaseItemType) {
    return new ShowcaseItem(md5_uuid(), getMemberAuth().getMemberId(), Date.now(), type);
  }

  static from(obj: any): ShowcaseItem {
    return JSON_OBJECT.deserializeObject(obj, ShowcaseItem);
  }

  constructor(id: string, creator: string, created: number, type: ShowcaseItemType) {
    super(id, creator, created);
    this.type = type;
    switch (this.type) {
      case ShowcaseItemType.IMAGE:
        this.imageData = new ImageData();
        break;
      case ShowcaseItemType.VIDEO:
        this.videoData = new VideoData();
        break;
      case ShowcaseItemType.AUDIO:
        this.audioData = new AudioData();
        break;
      case ShowcaseItemType.SCRATCH_PROJECT:
        this.scratchProjectData = new ScratchProjectData();
        break;
    }
  }

  private showcaseItemFavorites: ShowcaseItemFavorite[];
  private _favorited: boolean;

  favorited(): boolean {
    if (this._favorited !== undefined) {
      return this._favorited;
    }
    const memberAuth = getMemberAuth();
    if (!memberAuth?.auth?.currentUser) {
      return false;
    }
    const memberId = memberAuth.getMemberId();
    return Boolean(this.showcaseItemFavorites?.find(favorite => favorite.creator === memberId));
  }

  setFavorited(favorited: boolean): void {
    if (Boolean(this._favorited) === Boolean(favorited)) {
      return;
    }
    this._favorited = favorited;
    const loader = new ShowcaseItemFavorites(this.id);
    if (favorited) {
      loader.addListItem(ShowcaseItemFavorite.createNew())
    } else {
      loader.deleteListItemById(ShowcaseItemFavorite.createNew().id);
    }
  }

  getFavoritedByMembers(): Member[] {
    return this.showcaseItemFavorites.map(favorite => favorite.member);
  }

  protected defaultTitle(): string {
    return "Untitled";
  }

  async onAfterItemDeserialized(): Promise<void> {
    this.showcaseItemFavorites = await new ShowcaseItemFavorites(this.id).getOrLoadListItems();
    return super.onAfterItemDeserialized();
  }

  cloneShowcaseItem(): ShowcaseItem {
    return JSON_OBJECT.deserializeObject(JSON_OBJECT.serializeObject(this), ShowcaseItem);
  }
}

@JsonObject()
export class ShowcaseItemDraft extends ShowcaseItem {
  blob: Blob;
  coverImageBlob: Blob;

  static createNew(type: ShowcaseItemType) {
    return new ShowcaseItemDraft(md5_uuid(), getMemberAuth().getMemberId(), Date.now(), type);
  }

  cloneShowcaseItem(): ShowcaseItemDraft {
    const clone = JSON_OBJECT.deserializeObject(JSON_OBJECT.serializeObject(this), ShowcaseItemDraft);
    clone.blob = this.blob;
    clone.coverImageBlob = this.coverImageBlob;
    return clone;
  }
}

export class ShowcaseItems extends BaseListItemsLoader<ShowcaseItem> {

  private static instance;
  private static readonly memberInstances = new Map<string, ShowcaseItems>();

  static getInstance(): ShowcaseItems {
    if (!this.instance) {
      this.instance = new ShowcaseItems(getMemberAuth().getMemberId());
    }
    return this.instance;
  }

  static getMemberInstance(memberId: string): ShowcaseItems {
    let instance = this.memberInstances.get(memberId);
    if (!instance) {
      instance = new ShowcaseItems(memberId);
      this.memberInstances.set(memberId, instance);
    }
    return instance;
  }

  constructor(private readonly memberId: string) {
    super({shared: true});
  }

  protected basePath(): string {
    return "items/" + this.memberId;
  }

  protected deserializeItem(value: any): ShowcaseItem {
    console.log("des: " + value);
    return JSON_OBJECT.deserializeObject(value, ShowcaseItem);
  }

  protected serializeItem(item: ShowcaseItem): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: ShowcaseItem, item2: ShowcaseItem): number {
    // Reverse chronological
    return item2.created - item1.created;
  }
}

@JsonObject()
export class ShowcaseItemFavorite extends BaseListItem {

  static createNew(): ShowcaseItemFavorite {
    return new ShowcaseItemFavorite(getMemberAuth().getMemberId(), Date.now());
  }

  constructor(creator: string, created: number) {
    super(creator, creator, created);
  }
}

export class ShowcaseItemFavorites extends BaseListItemsLoader<ShowcaseItemFavorite> {

  constructor(readonly showcaseItemId: string) {
    super({shared: true});
  }

  protected basePath(): string {
    return "item_favorites/" + this.showcaseItemId;
  }

  protected deserializeItem(value: any): ShowcaseItemFavorite {
    return JSON_OBJECT.deserializeObject(value, ShowcaseItemFavorite);
  }

  protected serializeItem(item: ShowcaseItemFavorite): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: ShowcaseItemFavorite, item2: ShowcaseItemFavorite): number {
    return item1.created - item2.created;
  }
}

@JsonObject()
export class Album extends BaseItem {

  private showcaseItems: ShowcaseItem[];

  static createNew(title: string): Album {
    return new Album(md5_uuid(), getMemberAuth().getMemberId(), Date.now(), title);
  }

  constructor(id: string, creator: string, created: number, title: string) {
    super(id, creator, created, title);
  }

  protected defaultTitle(): string {
    return "New album";
  }
}

export class Albums extends BaseListItemsLoader<Album> {

  private static instance;
  private static readonly memberInstances = new Map<string, Albums>();

  static getInstance(): Albums {
    if (!this.instance) {
      this.instance = new Albums();
    }
    return this.instance;
  }

  static getMemberInstance(memberId: string): Albums {
    let instance = this.memberInstances.get(memberId);
    if (!instance) {
      instance = new Albums(memberId);
      this.memberInstances.set(memberId, instance);
    }
    return instance;
  }

  constructor(private readonly memberId?: string) {
    super({shared: Boolean(memberId)});
  }

  protected basePath(): string {
    return "albums" + (this.memberId ? "/" + this.memberId : "");
  }

  protected deserializeItem(value: any): Album {
    return JSON_OBJECT.deserializeObject(value, Album);
  }

  protected serializeItem(item: Album): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Album, item2: Album): number {
    // Reverse chronological
    return item2.created - item1.created;
  }
}

@JsonObject()
export class Playlist extends BaseItem {

  @JsonProperty()
  showcaseItemIds: string[]

  private showcaseItems: ShowcaseItem[];

  static createNew(title: string): Playlist {
    return new Playlist(md5_uuid(), getMemberAuth().getMemberId(), Date.now(), title);
  }

  constructor(id: string, creator: string, created: number, title: string) {
    super(id, creator, created, title);
  }

  protected defaultTitle(): string {
    return "New playlist";
  }

  clonePlaylist(): Playlist {
    return JSON_OBJECT.deserializeObject(JSON_OBJECT.serializeObject(this), Playlist);
  }

  async onAfterItemDeserialized(): Promise<void> {
    const showcaseItems: ShowcaseItem[] = [];
    if (this.showcaseItemIds) {
      for (const id of this.showcaseItemIds) {
        showcaseItems.push(await ShowcaseItems.getMemberInstance(this.creator).getOrLoadItem(id));
      }
    }
    this.showcaseItems = showcaseItems;
  }

  getShowcaseItems(): ShowcaseItem[] {
    return [...(this.showcaseItems || [])];
  }

  getShowcaseItemCoverUrls(): string[] {
    return [];
  }
}

export class Playlists extends BaseListItemsLoader<Playlist> {

  private static instance;
  private static readonly memberInstances = new Map<string, Playlists>();

  static getInstance(): Playlists {
    if (!this.instance) {
      this.instance = new Playlists();
    }
    return this.instance;
  }

  static getMemberInstance(memberId: string): Playlists {
    let instance = this.memberInstances.get(memberId);
    if (!instance) {
      instance = new Playlists(memberId);
      this.memberInstances.set(memberId, instance);
    }
    return instance;
  }

  constructor(private readonly memberId?: string) {
    super({shared: Boolean(memberId)});
  }

  protected basePath(): string {
    return "playlists" + (this.memberId ? "/" + this.memberId : "");
  }

  protected deserializeItem(value: any): Playlist {
    return JSON_OBJECT.deserializeObject(value, Playlist);
  }

  protected serializeItem(item: Playlist): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Playlist, item2: Playlist): number {
    // Reverse chronological
    return item2.created - item1.created;
  }
}

export interface QueueListener {
  onQueueUpdated(queue: Queue, reset?: boolean): void;
}

export class Queue {

  private static sInstance = new Queue();

  static getInstance(): Queue {
    return this.sInstance;
  }

  private showcaseItems: ShowcaseItem[] = [];
  private listeners: QueueListener[] = [];

  private constructor() {
  }

  addListener(listener: QueueListener) {
    this.listeners = [...this.listeners, listener];
  }

  removeListener(listener: QueueListener) {
    const index = this.listeners.findIndex(item => item === listener);
    if (index >= 0) {
      this.listeners.splice(index, 1);
    }
  }

  private notifyListeners(reset?: boolean) {
    this.listeners.forEach(listener => listener.onQueueUpdated(this, reset));
  }

  shuffle() {
    if (!this.showcaseItems?.length) {
      return;
    }
    const newShowcaseItems: ShowcaseItem[] = [];
    let array = Array.from(new Array(this.showcaseItems.length).keys());
    while (array.length > 0) {
      let index = Math.floor(Math.random() * array.length);
      newShowcaseItems.push(this.showcaseItems[array[index]]);
      array.splice(index, 1);
    }
    this.showcaseItems = newShowcaseItems;
    this.notifyListeners(true);
  }

  setShowcaseItems(showcaseItems: ShowcaseItem[]) {
    this.showcaseItems = showcaseItems;
    this.notifyListeners(true);
  }

  addShowcaseItem(showcaseItem: ShowcaseItem) {
    this.showcaseItems = [...this.showcaseItems, showcaseItem];
    this.notifyListeners();
  }

  getShowcaseItems(): ShowcaseItem[] {
    return this.showcaseItems;
  }
}

export enum EmbedType {
  NONE = "none",
  PLAYLIST = "playlist",
}

export type EmbedThemeOptions = {
  mode: PaletteMode,
  white: string,
  black: string,
  primary: string,
  secondary: string,
  bg1: string,
  bg2: string,
}

export const COLOR_YELLOW = "#ff0";
export const COLOR_CYAN = "#0ff";
export const COLOR_MAGENTA = "#f0f";

export const BOLT_EMBED_THEME_OPTIONS: EmbedThemeOptions = {
  mode: "light",
  white: "#eee",
  black: "#333",
  primary: COLOR_CYAN,
  secondary: COLOR_YELLOW,
  bg1: "#999",
  bg2: "#666",
}

export const LIGHT_EMBED_THEME_OPTIONS: EmbedThemeOptions = {
  mode: "light",
  white: "#fff",
  black: "#111",
  primary: "#111",
  secondary: "#099",
  bg1: "#fff",
  bg2: "#ccc",
}

@JsonObject()
export class EmbedConfig {

  @JsonProperty()
  readonly type: EmbedType;

  @JsonProperty()
  themeOptions?: EmbedThemeOptions;

  @JsonProperty()
  memberId?: string;

  @JsonProperty()
  playlistId?: string;

  constructor(type: EmbedType) {
    this.type = type;
  }
}